import { useEffect, useState } from "react";
import { Loader2, Wallet, CheckCircle2, Info } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Modal } from "@/components/Modal";
import { rupiah } from "@/lib/format";

const todayStr = () => new Date(Date.now() + 7 * 3600 * 1000).toISOString().slice(0, 10);

export function PaymentDialog({ inv, onClose, onSaved }) {
  const [amount, setAmount] = useState(inv.amount_residual);
  const [date, setDate] = useState(todayStr());
  const [journals, setJournals] = useState([]);
  const [journalsLoading, setJournalsLoading] = useState(true);
  const [journal, setJournal] = useState("");
  const [ref, setRef] = useState("");
  const [saving, setSaving] = useState(false);
  const amt = Number(amount) || 0;
  const full = amt >= inv.amount_residual;
  const needJournal = !!inv.odoo_move_id;
  const valid = amt > 0 && amt <= inv.amount_residual && (!needJournal || !!journal);

  useEffect(() => {
    api.get("/finance/journals").then((r) => {
      setJournals(r.data.data);
      if (r.data.data[0]) setJournal(String(r.data.data[0].odoo_id));
    }).catch(() => {}).finally(() => setJournalsLoading(false));
  }, []);

  const submit = async () => {
    if (needJournal && !journal) { toast.error("Pilih jurnal penerimaan (Bank/Kas) Odoo"); return; }
    if (!valid) { toast.error(`Nominal harus 1 – ${rupiah(inv.amount_residual)}`); return; }
    setSaving(true);
    const j = journals.find((x) => String(x.odoo_id) === journal);
    try {
      const r = await api.post(`/finance/invoices/${inv.id}/payments`, {
        amount: amt, payment_date: date, journal_odoo_id: j ? j.odoo_id : null, journal_name: j ? j.name : "Manual", reference: ref || null,
      });
      toast.success(`Pembayaran diverifikasi · status ${r.data.data.status}`);
      onSaved(r.data.data);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} className="fin-dialog" title={`Verifikasi Pembayaran ${inv.number}`} subtitle={inv.outlet} testId="payment-dialog"
      footer={<>
        <button className="rj-btn rj-btn-ghost" onClick={onClose} data-testid="payment-cancel-btn">Batal</button>
        <button className="rj-btn rj-btn-green" onClick={submit} disabled={saving || journalsLoading || !valid} data-testid="payment-submit-btn">
          {saving ? <Loader2 size={16} className="spin" /> : <><CheckCircle2 size={16} /> Verifikasi Pembayaran</>}
        </button>
      </>}>
      <div className="fin-pay-head">
        <div><span>Total tagihan</span><b>{rupiah(inv.amount_total)}</b></div>
        <div><span>Sudah dibayar</span><b>{rupiah(inv.amount_paid)}</b></div>
        <div className="due"><span>Sisa tagihan</span><b data-testid="payment-residual">{rupiah(inv.amount_residual)}</b></div>
      </div>
      <div className="fin-form-grid">
        <label className="rj-field">Nominal diterima (Rp)
          <input type="number" className="rj-input" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="payment-amount" />
        </label>
        <div className="fin-quick">
          <button type="button" className="dist-toggle" onClick={() => setAmount(inv.amount_residual)} data-testid="payment-fill-full">Isi lunas</button>
          <button type="button" className="dist-toggle" onClick={() => setAmount(Math.round(inv.amount_residual / 2))} data-testid="payment-fill-half">50%</button>
        </div>
        <label className="rj-field">Tanggal bayar
          <input type="date" className="rj-input" value={date} onChange={(e) => setDate(e.target.value || todayStr())} data-testid="payment-date" />
        </label>
        <label className="rj-field">Diterima di (jurnal Odoo)
          <select className="rj-select" value={journal} onChange={(e) => setJournal(e.target.value)} disabled={journalsLoading} data-testid="payment-journal">
            {journalsLoading && <option value="">Memuat jurnal Odoo…</option>}
            {!journalsLoading && journals.length === 0 && <option value="">{needJournal ? "Jurnal Odoo tidak tersedia" : "Manual (tanpa Odoo)"}</option>}
            {journals.map((j) => <option key={j.odoo_id} value={j.odoo_id}>{j.name} ({j.type === "bank" ? "Bank" : "Kas"})</option>)}
          </select>
        </label>
        <label className="rj-field fin-span">Referensi / catatan
          <input className="rj-input" value={ref} onChange={(e) => setRef(e.target.value)} placeholder="cth: Transfer BCA 27/10" data-testid="payment-reference" />
        </label>
      </div>
      <div className={`fin-info ${full ? "ok" : ""}`} data-testid="payment-kind">
        {full ? <CheckCircle2 size={15} /> : <Info size={15} />}
        <span>{full ? "Pembayaran penuh — status invoice menjadi Lunas." : `Pembayaran sebagian — sisa ${rupiah(Math.max(inv.amount_residual - amt, 0))}, status menjadi Dibayar Sebagian.`}
          {inv.odoo_move_id ? " Pembayaran juga dicatat di Odoo." : ""}</span>
      </div>
      <p className="fin-muted" style={{ marginTop: 8 }}><Wallet size={12} /> Hanya catat pembayaran yang dananya sudah diterima.</p>
    </Modal>
  );
}
